/**
 * Utility for parsing markdown content into collapsible sections
 * Used by CollapsibleMarkdown component for Dhyana chat AI responses
 */

export interface MarkdownSection {
  id: string;           // Unique identifier for React keys
  header: string;       // Full header text including emoji (e.g., "🎯 Answer")
  emoji: string;        // Just the emoji (e.g., "🎯")
  title: string;        // Just the title (e.g., "Answer")
  content: string;      // Content under this section (markdown)
  isMainAnswer: boolean; // True for the first/answer section
}

export interface ParsedMarkdown {
  sections: MarkdownSection[];
  hasMultipleSections: boolean;
  rawContent: string;
  introText: string;  // Text before the first section header (if any)
}

/**
 * Parse markdown content into sections based on ## headers with emojis
 *
 * Expected format from backend:
 * - ## 🎯 Answer (main content - always present)
 * - ## 🔗 Connections (optional)
 * - ## 📸 Visual Guide (optional)
 * - ## 📝 Your Notes (optional)
 *
 * @param content - Raw markdown content from AI response
 * @returns Parsed markdown with sections array
 */
export function parseMarkdownSections(content: string): ParsedMarkdown {
  if (!content || typeof content !== 'string') {
    return {
      sections: [],
      hasMultipleSections: false,
      rawContent: content || '',
      introText: ''
    };
  }

  // Regex to match section headers: ## 🎯 Answer, ## 🔗 Connections, etc.
  // Matches: ## followed by space, emoji (Unicode), space, and title text
  // The emoji range covers common emoji used: 🎯 🔗 📸 📝
  const sectionHeaderRegex = /^#{2,3}\s+([\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])\s+(.+)$/gmu;

  // Find all section headers and their positions
  const matches: { index: number; fullMatch: string; emoji: string; title: string }[] = [];
  let match;

  while ((match = sectionHeaderRegex.exec(content)) !== null) {
    matches.push({
      index: match.index,
      fullMatch: match[0],
      emoji: match[1],
      title: match[2].trim()
    });
  }

  // If no sections found or only one section, return as non-collapsible
  if (matches.length <= 1) {
    return {
      sections: [],
      hasMultipleSections: false,
      rawContent: content,
      introText: ''
    };
  }

  const sections: MarkdownSection[] = [];

  // Capture any content before the first section header
  let contentBeforeFirstSection = '';
  if (matches[0].index > 0) {
    contentBeforeFirstSection = content.substring(0, matches[0].index).trim();
  }

  // Extract content for each section
  matches.forEach((m, idx) => {
    const startIndex = m.index + m.fullMatch.length;
    const endIndex = idx < matches.length - 1 ? matches[idx + 1].index : content.length;
    const sectionContent = content.substring(startIndex, endIndex).trim();

    sections.push({
      id: `section-${idx}-${m.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`,
      header: `${m.emoji} ${m.title}`,
      emoji: m.emoji,
      title: m.title,
      content: sectionContent,
      isMainAnswer: idx === 0 // First section is always the main answer
    });
  });

  return {
    sections,
    hasMultipleSections: sections.length > 1,
    rawContent: content,
    introText: contentBeforeFirstSection
  };
}

/**
 * Get friendly display name for section titles
 * Makes the UI more engaging with Zomato-style witty copy
 */
export function getFriendlyTitle(title: string): string {
  const lowerTitle = title.toLowerCase();

  if (lowerTitle.includes('answer')) {
    return "Here's the deal";
  }

  if (lowerTitle.includes('connections') || lowerTitle.includes('connection')) {
    return 'Connecting the dots';
  }

  if (lowerTitle.includes('visual')) {
    return 'Picture this';
  }

  if (lowerTitle.includes('notes') || lowerTitle.includes('note')) {
    return 'From your notes';
  }

  if (lowerTitle.includes('lock') || lowerTitle.includes('cheat')) {
    return 'Exam cheat codes';
  }

  // Revision-mode friendlies (most-specific first so substrings don't shadow).
  if (lowerTitle.includes('speed run')) {
    return 'Speed run';
  }

  if (lowerTitle.includes('concept overview')) {
    return 'The pattern you need';
  }

  if (lowerTitle.includes('walkthrough') || lowerTitle.includes('simplified')) {
    return 'Walk it through';
  }

  if (lowerTitle.includes('real world')) {
    return 'In the wild';
  }

  if (lowerTitle.includes('dodo')) {
    return "Don't be a dodo";
  }

  if (lowerTitle.includes('next move')) {
    return 'Your next move';
  }

  if (lowerTitle.includes('thread')) {
    return 'The through-line';
  }

  // Return original if no match
  return title;
}

/**
 * Get section-specific styling based on section title
 * Used for color-coding different sections
 */
export function getSectionStyles(title: string): {
  headerBg: string;
  headerBorder: string;
  headerText: string;
  accentColor: string;
  highlight?: boolean;
} {
  const lowerTitle = title.toLowerCase();

  if (lowerTitle.includes('answer')) {
    return {
      headerBg: 'bg-cyan-50 dark:bg-cyan-900/20',
      headerBorder: 'border-cyan-200 dark:border-cyan-700/50',
      headerText: 'text-cyan-700 dark:text-cyan-300',
      accentColor: 'cyan'
    };
  }

  if (lowerTitle.includes('connections') || lowerTitle.includes('connection')) {
    return {
      headerBg: 'bg-purple-50 dark:bg-purple-900/20',
      headerBorder: 'border-purple-200 dark:border-purple-700/50',
      headerText: 'text-purple-700 dark:text-purple-300',
      accentColor: 'purple',
      highlight: true
    };
  }

  if (lowerTitle.includes('visual')) {
    return {
      headerBg: 'bg-amber-50 dark:bg-amber-900/20',
      headerBorder: 'border-amber-200 dark:border-amber-700/50',
      headerText: 'text-amber-700 dark:text-amber-300',
      accentColor: 'amber'
    };
  }

  if (lowerTitle.includes('notes') || lowerTitle.includes('note')) {
    return {
      headerBg: 'bg-emerald-50 dark:bg-emerald-900/20',
      headerBorder: 'border-emerald-200 dark:border-emerald-700/50',
      headerText: 'text-emerald-700 dark:text-emerald-300',
      accentColor: 'emerald'
    };
  }

  if (lowerTitle.includes('lock') || lowerTitle.includes('cheat')) {
    return {
      headerBg: 'bg-rose-50 dark:bg-rose-900/20',
      headerBorder: 'border-rose-200 dark:border-rose-700/50',
      headerText: 'text-rose-700 dark:text-rose-300',
      accentColor: 'rose'
    };
  }

  // Revision-mode sections — ordered most-specific to least-specific so
  // substrings don't shadow each other (e.g., `speed run` before `next move`
  // since Speed Run output ends with `## ✨ Your Next Move`).

  if (lowerTitle.includes('speed run')) {
    return {
      headerBg: 'bg-fuchsia-50 dark:bg-fuchsia-900/20',
      headerBorder: 'border-fuchsia-200 dark:border-fuchsia-700/50',
      headerText: 'text-fuchsia-700 dark:text-fuchsia-300',
      accentColor: 'fuchsia'
    };
  }

  if (lowerTitle.includes('concept overview')) {
    return {
      headerBg: 'bg-indigo-50 dark:bg-indigo-900/20',
      headerBorder: 'border-indigo-200 dark:border-indigo-700/50',
      headerText: 'text-indigo-700 dark:text-indigo-300',
      accentColor: 'indigo'
    };
  }

  if (lowerTitle.includes('walkthrough') || lowerTitle.includes('simplified')) {
    return {
      headerBg: 'bg-sky-50 dark:bg-sky-900/20',
      headerBorder: 'border-sky-200 dark:border-sky-700/50',
      headerText: 'text-sky-700 dark:text-sky-300',
      accentColor: 'sky'
    };
  }

  if (lowerTitle.includes('real world')) {
    return {
      headerBg: 'bg-orange-50 dark:bg-orange-900/20',
      headerBorder: 'border-orange-200 dark:border-orange-700/50',
      headerText: 'text-orange-700 dark:text-orange-300',
      accentColor: 'orange'
    };
  }

  if (lowerTitle.includes('dodo')) {
    return {
      headerBg: 'bg-red-50 dark:bg-red-900/20',
      headerBorder: 'border-red-200 dark:border-red-700/50',
      headerText: 'text-red-700 dark:text-red-300',
      accentColor: 'red'
    };
  }

  if (lowerTitle.includes('next move')) {
    return {
      headerBg: 'bg-teal-50 dark:bg-teal-900/20',
      headerBorder: 'border-teal-200 dark:border-teal-700/50',
      headerText: 'text-teal-700 dark:text-teal-300',
      accentColor: 'teal'
    };
  }

  if (lowerTitle.includes('thread')) {
    return {
      headerBg: 'bg-violet-50 dark:bg-violet-900/20',
      headerBorder: 'border-violet-200 dark:border-violet-700/50',
      headerText: 'text-violet-700 dark:text-violet-300',
      accentColor: 'violet'
    };
  }

  // Default styling for unknown sections
  return {
    headerBg: 'bg-slate-50 dark:bg-slate-800/50',
    headerBorder: 'border-slate-200 dark:border-slate-700/50',
    headerText: 'text-slate-700 dark:text-slate-300',
    accentColor: 'slate'
  };
}
